import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { SortInput } from 'app/generated/graphql'
import { addListItems, moveListItem, readListPage, removeListItem } from 'app/lib/listApi'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import type { ContactJson } from 'app/types/entities'
import type { ListBoardState, StageOutcome } from 'app/types/list-board'
import { bg } from 'lib/background'
import { INDEX_SETTLE_MS, PLACED_TTL_MS, placementsFor, type ListPlacement } from 'lib/listPlacements'
import { announceListWrite } from 'lib/listWrites'
import { logError } from 'lib/logging'
import { NO_STAGE, laneOf, withAdded, withMove, withPage, withRemoved, type LanesState } from 'lib/manualListLanes'

export const LIST_PAGE_SIZE = 25

/**
 * Where the user last put a contact, ahead of the search index confirming it. `stage: null`
 * is a tombstone left by a successful remove: the contact belongs in NO lane, not just a
 * different one. The map is per list and lives outside this hook, so a stage set in the
 * table's own cell is on the board the moment it reads again.
 */
type Placement = ListPlacement<ContactJson>

/**
 * Lets go of any placement the index has had more than `PLACED_TTL_MS` to catch up on. Takes
 * `now` from the caller so a single reload's parallel lane reads agree on one clock: each lane
 * calling `Date.now()` for itself could see the TTL boundary fall between two lanes' completion
 * times, leaving a card shown in two lanes, or in none, until the next reload.
 */
function purgeExpiredPlacements(placed: Map<string, Placement>, now: number): void {
  for (const [id, entry] of placed) {
    if (now - entry.at > PLACED_TTL_MS) placed.delete(id)
  }
}

export function useListBoard(opts: {
  listId: string | undefined
  stageIds: readonly string[]
  filterTerms: readonly string[]
  /** False while the location filter is still resolving: reading then would show every location. */
  ready: boolean
  sort?: SortInput
}): ListBoardState {
  const { listId, ready } = opts
  const [lanes, setLanes] = useState<LanesState<ContactJson>>({})
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [loadingLanes, setLoadingLanes] = useState<Set<string>>(new Set())
  const [error, setError] = useState<string>()
  const [failedLanes, setFailedLanes] = useState<ReadonlySet<string>>(new Set())
  const [nonce, setNonce] = useState(0)
  const generation = useRef(0)

  const stageIdsKey = JSON.stringify(opts.stageIds)
  const filterKey = opts.filterTerms.join(` AND `)
  // Keyed on the contents, not the array's identity, because callers build a fresh array every render.
  const stageIds = useMemo(
    () => opts.stageIds,
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- keyed on stageIdsKey (JSON of opts.stageIds) on purpose
    [stageIdsKey],
  )

  const sortKey = opts.sort ? `${opts.sort.field}:${opts.sort.direction}` : ``
  const sort = useMemo(
    () => opts.sort,
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- keyed on sortKey (field and direction of opts.sort) on purpose
    [sortKey],
  )

  // A different list is a different board: the old cards must stop being draggable (a drag would
  // write them into the NEW list) before the new list's own reads start. Bumping the generation
  // here, not just in the read effect, matters because the read effect returns early whenever
  // `!listId || !ready`: without this, a read already in flight for the OLD list would still
  // pass the generation check and repaint the board it was just cleared from.
  // Placements need no reset: they are per list, so this board simply starts reading the new
  // list's own map, and wiping anything here would throw away the list it just left.
  useEffect(() => {
    generation.current += 1
    setLanes({})
    setTotal(0)
    setError(undefined)
    setLoadingLanes(new Set())
    setFailedLanes(new Set())
    setLoading(false)
  }, [listId])

  // A deleted stage leaves a placement keyed to an id that is no longer a lane: it would survive
  // the TTL (a reload only happens on user action), and `readLane`'s stale-hit filter would keep
  // dropping the hit that proves the delete went through. A tombstone (`stage: null`) is left
  // alone: it means "no stage", not "a stage that no longer exists".
  useEffect(() => {
    if (!listId) return
    const placed = placementsFor<ContactJson>(listId)
    const currentStages = new Set(stageIds)
    for (const [id, entry] of placed) {
      if (entry.stage !== null && !currentStages.has(entry.stage)) placed.delete(id)
    }
  }, [stageIds, listId])

  const readLane = useCallback(
    async (laneKey: string, page: number, gen: number) => {
      if (!listId) return
      const { contacts, total: laneTotal } = await readListPage({
        listId,
        stageId: laneKey === NO_STAGE ? undefined : laneKey,
        search: filterKey || undefined,
        sort,
        page,
        limit: LIST_PAGE_SIZE,
      })
      if (gen !== generation.current) return
      // The caller purges once, with one clock, before kicking off every parallel lane read for
      // this cycle (see the read effect and `loadMore`): reading `Date.now()` again per lane here
      // would risk splitting the TTL boundary across lanes that finish at slightly different times.
      // Contacts placed in this lane by an add or a move: they belong here even if the read
      // does not (yet) return them. A tombstone (`stage: null`) never matches a lane, so it is
      // never handed on as something to keep showing.
      const placed = placementsFor<ContactJson>(listId)
      const placedHere = [...placed.values()].filter((entry) => entry.stage === laneKey).map((entry) => entry.item)
      // A hit the user placed in a DIFFERENT lane, or removed (a tombstone), is the search index
      // still lagging that write: trust the placement, not the stale hit, and keep the lane's
      // total honest about it.
      let stale = 0
      const survivors = contacts.filter((contact) => {
        const entry = contact.id ? placed.get(contact.id) : undefined
        if (entry && entry.stage !== laneKey) {
          stale += 1
          return false
        }
        return true
      })
      // A hit this lane confirms is a placement the index has caught up with. A tombstone
      // never confirms (its stage never equals a real laneKey), so it only clears on the TTL.
      for (const contact of contacts) {
        if (contact.id && placed.get(contact.id)?.stage === laneKey) placed.delete(contact.id)
      }
      setLanes((previous) => {
        // A later page can only extend the lane it was requested against. If a reload reset the
        // lane underneath it, or this page already landed, applying it here would either leave a
        // gap earlier pages never heal or double an already-appended page.
        if (page > 0 && previous[laneKey]?.page !== page - 1) return previous
        return withPage(previous, laneKey, page, survivors, Math.max(0, laneTotal - stale), LIST_PAGE_SIZE, placedHere)
      })
    },
    [listId, filterKey, sort],
  )

  useEffect(() => {
    if (!listId || !ready) return
    // A manual list with no stages yet still shows its members in the one No stage lane.
    const laneKeys = stageIds.length > 0 ? stageIds : [NO_STAGE]
    const placed = placementsFor<ContactJson>(listId)
    const gen = ++generation.current
    setLoading(true)
    setError(undefined)
    setLoadingLanes(new Set())
    setFailedLanes(new Set())
    // One clock for every lane this cycle reads in parallel, so they all agree on what has expired.
    purgeExpiredPlacements(placed, Date.now())
    // A lane's own catch records it in `failedLanes` and rethrows, so the `Promise.all` below
    // still sees the rejection and sets the banner exactly once, in its own single `.catch`.
    const readLaneOrMark = (laneKey: string) =>
      readLane(laneKey, 0, gen).catch((err) => {
        if (gen === generation.current) setFailedLanes((previous) => new Set(previous).add(laneKey))
        throw err
      })
    const totalRead = readListPage({ listId, search: filterKey || undefined, sort, page: 0, limit: 0 }).then(
      ({ total: listTotal }) => {
        if (gen !== generation.current) return
        // A live tombstone has already been subtracted from every lane's total; keep the
        // header count agreeing with the lanes while the index still counts the removal.
        const tombstones = [...placed.values()].filter((entry) => entry.stage === null).length
        setTotal(Math.max(0, listTotal - tombstones))
      },
    )
    bg(
      Promise.all([...laneKeys.map((laneKey) => readLaneOrMark(laneKey)), totalRead])
        .catch((err) => {
          if (gen !== generation.current) return
          logError(`Error loading a list's board`, err)
          setError(`Couldn't load this list. Please try again.`)
        })
        .finally(() => {
          if (gen === generation.current) setLoading(false)
        }),
    )
  }, [listId, ready, stageIds, filterKey, nonce, readLane])

  const loadMore = useCallback(
    (laneKey: string) => {
      // A load in flight is about to reset every lane's page to 0; a page requested against the
      // lane as it stands now would either land on a reset lane or double up once it does.
      if (loading) return
      if (!listId) return
      const lane = lanes[laneKey]
      if (!lane?.hasMore) return
      // A separate user action from a reload: it gets its own clock rather than reusing a cycle's.
      purgeExpiredPlacements(placementsFor<ContactJson>(listId), Date.now())
      setLoadingLanes((previous) => new Set(previous).add(laneKey))
      bg(
        readLane(laneKey, lane.page + 1, generation.current)
          .catch((err) => {
            logError(`Error loading more of a list lane`, laneKey, err)
            notifyError(`Couldn't load more. Please try again.`)
          })
          .finally(() =>
            setLoadingLanes((previous) => {
              const next = new Set(previous)
              next.delete(laneKey)
              return next
            }),
          ),
      )
    },
    [lanes, loading, readLane, listId],
  )

  const findContact = (contactId: string) =>
    Object.values(lanes)
      .flatMap((lane) => lane.items)
      .find((contact) => contact.id === contactId)

  const move = async (contactId: string, stageId: string, opts?: { quiet?: boolean }): Promise<StageOutcome> => {
    const contact = findContact(contactId)
    const fromKey = laneOf(lanes, contactId)
    if (!contact || !listId) return { ok: false, reason: `This contact is no longer on screen` }
    // A write to the same stage still rewrites a system list's status field, and that can fire workflows.
    if (fromKey === stageId) return { ok: false, skipped: true, reason: `Already at this stage` }
    const gen = generation.current
    const placed = placementsFor<ContactJson>(listId)
    const previousPlacement = placed.get(contactId)
    placed.set(contactId, { stage: stageId, item: contact, at: Date.now() })
    setLanes((previous) => withMove(previous, contactId, laneOf(previous, contactId), stageId, contact))
    try {
      await moveListItem(listId, contactId, stageId)
      return { ok: true }
    } catch (err) {
      logError(`Error moving a contact within a list`, err)
      if (!opts?.quiet) notifyError(`Couldn't save that move. It's back where it was.`)
      // A failed write means the server still holds the old state, so a board that has moved on
      // (a reload, a list switch) needs no overlay entry to land on the truth either: restoring
      // it unconditionally would leave this list's map holding a placement no board asked for,
      // waiting to mislead the next visit inside the TTL.
      if (gen === generation.current) {
        if (previousPlacement) placed.set(contactId, previousPlacement)
        else placed.delete(contactId)
        setLanes((previous) =>
          fromKey
            ? withMove(previous, contactId, laneOf(previous, contactId), fromKey, contact)
            : withRemoved(previous, contactId),
        )
      }
      return { ok: false, reason: err instanceof Error ? err.message : `Couldn't save that move` }
    }
  }

  const remove = async (contactId: string) => {
    const contact = findContact(contactId)
    if (!contact || !listId) return false
    const gen = generation.current
    const placed = placementsFor<ContactJson>(listId)
    const fromKey = laneOf(lanes, contactId)
    const previousPlacement = placed.get(contactId)
    setLanes((previous) => withRemoved(previous, contactId))
    setTotal((previous) => Math.max(0, previous - 1))
    try {
      await removeListItem(listId, contactId)
      // A tombstone: a reload during the index lag must not draw the card back into its old lane.
      placed.set(contactId, { stage: null, item: contact, at: Date.now() })
      return true
    } catch (err) {
      logError(`Error removing a contact from a list`, err)
      notifyError(`Couldn't remove them from the list. They're back where they were.`)
      // Same reasoning as `move`'s catch: a board that has moved on gets no overlay write either.
      if (gen === generation.current) {
        if (previousPlacement) placed.set(contactId, previousPlacement)
        else placed.delete(contactId)
        setLanes((previous) => (fromKey ? withAdded(previous, fromKey, [contact]) : previous))
        setTotal((previous) => previous + 1)
      }
      return false
    }
  }

  // The server already took the contact off the list (a Closed Lost milestone, say): drop the card and
  // leave a tombstone so a read inside the index's lag does not draw it back. Nothing is written.
  const forget = (contactId: string) => {
    const contact = findContact(contactId)
    if (!contact || !listId) return
    placementsFor<ContactJson>(listId).set(contactId, { stage: null, item: contact, at: Date.now() })
    setLanes((previous) => withRemoved(previous, contactId))
    setTotal((previous) => Math.max(0, previous - 1))
    announceListWrite()
  }

  const add = async (contacts: ContactJson[], stageId: string) => {
    if (!listId || contacts.length === 0) return
    const gen = generation.current
    const ids = contacts.flatMap((contact) => contact.id ?? [])
    const { added: addedIds } = await addListItems(listId, ids, stageId)
    const addedSet = new Set(addedIds)
    const added = contacts.filter((contact) => contact.id && addedSet.has(contact.id))
    const failed = contacts.length - added.length
    // A list change mid-flight means these writes landed on the OLD list: the new list's
    // overlay and lanes must not be seeded with contacts that were never added to it. The
    // writes themselves already happened, so the toasts below still reflect the truth.
    if (gen === generation.current) {
      const placed = placementsFor<ContactJson>(listId)
      for (const contact of added) {
        if (contact.id) placed.set(contact.id, { stage: stageId, item: contact, at: Date.now() })
      }
      setLanes((previous) => withAdded(previous, stageId, added))
      setTotal((previous) => previous + added.length)
    }
    if (added.length > 0) notifySuccess(`Added ${added.length} contact${added.length === 1 ? `` : `s`}`)
    if (failed > 0) notifyError(`${failed} contact${failed === 1 ? `` : `s`} couldn't be added`)
  }

  // A stage's members can be rewritten outside this hook, and the reload that follows lands
  // inside the index's lag: without a placement, the stage reads as empty and nothing reads
  // again until the user reloads.
  const settleTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  useEffect(() => () => clearTimeout(settleTimer.current), [])
  const place = useCallback(
    (contacts: ContactJson[], stageId: string) => {
      if (!listId) return
      const placed = placementsFor<ContactJson>(listId)
      const at = Date.now()
      for (const contact of contacts) {
        if (contact.id) placed.set(contact.id, { stage: stageId, item: contact, at })
      }
      clearTimeout(settleTimer.current)
      settleTimer.current = setTimeout(() => setNonce((previous) => previous + 1), INDEX_SETTLE_MS)
    },
    [listId],
  )

  const reload = useCallback(() => setNonce((previous) => previous + 1), [])

  return {
    lanes,
    total,
    loading,
    loadingLanes,
    error,
    failedLanes,
    loadMore,
    move,
    add,
    remove,
    forget,
    place,
    reload,
  }
}
